import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import AssignUserModal from "./AssignUserModal";
import { useAuthStore } from "../store/useAuthStore";
import { api } from "./api";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getSocket } from "../socket";
import "./ProjectsSidebar.css";

/** ===== Deploy-friendly perf knobs ===== */
const TASK_COUNTS_MODE = "topN"; // "topN" | "selected" | "all"
const TOPN_COUNT = 12; // topN projects only to avoid N*2 calls
const COUNTS_BATCH_SIZE = 6; // parallel API window (avoid flood)
const MAX_COUNTS_PROJECTS = 100; // optional hard cap to avoid storms

export default function ProjectsSidebar({
  projects = [],
  loading,
  selectedProject,
  setSelectedProject,
  refreshProjects,
  isMobile = false,
  onVisibleProjectsChange = function () {},
  onAfterAssign = function () {},
  onBack,
  /** Auto-open support (highest priority after URL/state) */
  initialOpenProjectId,
  initialOpenProjectName,
}) {
  /* ---------------- state ---------------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");

  const [assignModalProject, setAssignModalProject] = useState(null);
  const [users] = useState([]); // prop-shape only
  const [drawingMaster] = useState([]); // prop-shape only

  const [projectTaskCounts, setProjectTaskCounts] = useState({});
  const [employeeWorkedProjectNames, setEmployeeWorkedProjectNames] = useState(
    [],
  );

  // meta: { [projectId]: { unread, lastText, lastSender, lastAt } }
  const [msgMeta, setMsgMeta] = useState({});

  const [showSpinner, setShowSpinner] = useState(false);
  const didAutoSelectRef = useRef(false); // prevent repeated auto-selects

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let t;
    if (loading)
      t = setTimeout(() => {
        setShowSpinner(true);
      }, 300);
    else setShowSpinner(false);
    return () => {
      if (t) clearTimeout(t);
    };
  }, [loading]);

  // debounce search (keep UI snappy on deploy)
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(searchTerm.trim().toLowerCase());
    }, 250);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const { authUser } = useAuthStore();
  const role = String((authUser && authUser.role) || "").toLowerCase();
  const isAdmin = role === "admin";
  const isClient = role === "client";
  const isEmployee = !isAdmin && !isClient;
  const canAssign = isAdmin || isEmployee;

  // Harden header so we never send "Bearer null"
  const token = localStorage.getItem("token") || "";
  const authHeader = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : { headers: {} };

  const getProjName = (p) =>
    String(p.projectName || p.name || "Untitled Project").trim();

  /* ---------------- visible projects ---------------- */
  useEffect(() => {
    if (!isEmployee || !(authUser && authUser._id)) return;
    api
      .get("/logs/my", authHeader)
      .then((res) => {
        const logs = Array.isArray(res.data) ? res.data : [];
        const names = logs
          .map((l) => String(l.projectName || "").toLowerCase())
          .filter(Boolean);
        const unique = Array.from(new Set(names));
        setEmployeeWorkedProjectNames(unique);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser && authUser._id, isEmployee]);

  const visibleProjects = useMemo(() => {
    if (isAdmin) return projects;

    const myId = authUser && authUser._id ? authUser._id : "";
    const isAssignedToMe = (p) => {
      const list = Array.isArray(p.assignedUsers) ? p.assignedUsers : [];
      for (let i = 0; i < list.length; i++) {
        const u = list[i];
        if (typeof u === "string") {
          if (u === myId) return true;
        } else if (u && String(u._id) === String(myId)) {
          return true;
        }
      }
      return false;
    };

    if (isClient) return projects.filter(isAssignedToMe);

    const workedSet = new Set(employeeWorkedProjectNames);
    const assigned = projects.filter(isAssignedToMe);
    const worked = projects.filter((p) =>
      workedSet.has(getProjName(p).toLowerCase()),
    );

    const map = new Map();
    assigned.forEach((p) => map.set(String(p._id), p));
    worked.forEach((p) => map.set(String(p._id), p));
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projects,
    authUser && authUser._id,
    isAdmin,
    isClient,
    isEmployee,
    employeeWorkedProjectNames,
  ]);

  useEffect(() => {
    onVisibleProjectsChange(visibleProjects);
  }, [onVisibleProjectsChange, visibleProjects]);

  /* ---------------- task counts (deploy-friendly) ---------------- */
  useEffect(() => {
    let cancelled = false;

    async function fetchCountsDeployFriendly(list) {
      // Decide which projects to fetch counts for
      let targets = [];
      if (TASK_COUNTS_MODE === "selected" && selectedProject) {
        targets = [selectedProject];
      } else if (TASK_COUNTS_MODE === "topN") {
        targets = list.slice(0, TOPN_COUNT);
      } else {
        targets = list;
      }

      if (!targets.length) {
        setProjectTaskCounts({});
        return;
      }

      // Optional: skip counts if too many visible projects (avoid storms)
      if (list.length > MAX_COUNTS_PROJECTS) {
        setProjectTaskCounts({});
        return;
      }

      const headers = authHeader.headers;
      const results = {};
      const chunks = [];
      for (let i = 0; i < targets.length; i += COUNTS_BATCH_SIZE) {
        chunks.push(targets.slice(i, i + COUNTS_BATCH_SIZE));
      }
      for (const batch of chunks) {
        const batchRes = await Promise.all(
          batch.map(async (p) => {
            try {
              const [pend, comp] = await Promise.all([
                api.get("/tasks/project/" + p._id + "?status=pending", {
                  headers,
                }),
                api.get("/tasks/project/" + p._id + "?status=completed", {
                  headers,
                }),
              ]);
              return [
                p._id,
                {
                  pending: Array.isArray(pend.data) ? pend.data.length : 0,
                  completed: Array.isArray(comp.data) ? comp.data.length : 0,
                },
              ];
            } catch {
              return [p._id, { pending: 0, completed: 0 }];
            }
          }),
        );
        batchRes.forEach(([id, obj]) => (results[id] = obj));
        // yield to UI
        await new Promise((r) => setTimeout(r, 0));
      }
      if (cancelled) return;

      // single state update (important for deploy perf)
      setProjectTaskCounts((prev) => ({ ...prev, ...results }));
    }

    fetchCountsDeployFriendly(visibleProjects);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjects, selectedProject && selectedProject._id]);

  /* ---------------- unread / last message helpers ---------------- */
  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const t = new Date();
      const sameDay =
        d.getFullYear() === t.getFullYear() &&
        d.getMonth() === t.getMonth() &&
        d.getDate() === t.getDate();
      return sameDay
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-GB");
    } catch (e) {
      return "";
    }
  };

  const previewFromMessage = (m = {}) => {
    const s = m.sender || {};
    const sender = s.name || s.username || s.email || m.senderLabel || "";
    if (m.fileName || m.file) {
      const seg = String(m.file || "").split("/");
      const fname = m.fileName || (seg.length ? seg[seg.length - 1] : "file");
      return { lastSender: sender, lastText: "📎 " + fname };
    }
    const text = String(m.content || m.text || "")
      .replace(/\s+/g, " ")
      .trim();
    return { lastSender: sender, lastText: text.slice(0, 60) };
  };

  const hydrateMetaBatch = useCallback(async (ids) => {
    if (!ids.length) return {};
    const headers = authHeader.headers;

    try {
      const res = await api.post(
        "/project-messages/summary",
        { projectIds: ids },
        { headers, timeout: 15000 },
      );

      const arr = Array.isArray(res.data) ? res.data : [];
      const out = {};

      arr.forEach((row) => {
        const projectId = row.projectId;
        const unread = Number(row.unread || 0);
        const lastMessage = row.lastMessage || null;
        const lastAt =
          row.lastAt || (lastMessage && lastMessage.createdAt) || null;

        const pv = previewFromMessage(lastMessage || {});
        out[projectId] = {
          unread,
          lastText: pv.lastText || "",
          lastSender: pv.lastSender || "",
          lastAt,
        };
      });

      return out;
    } catch (e) {
      // fallback: only unread-count
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const cRes = await api.get(
              "/project-messages/" + id + "/unread-count",
              { headers, timeout: 12000 },
            );

            return [
              id,
              {
                unread: Number(cRes?.data?.unread || 0),
                lastText: "",
                lastSender: "",
                lastAt: null,
              },
            ];
          } catch {
            return [
              id,
              { unread: 0, lastText: "", lastSender: "", lastAt: null },
            ];
          }
        }),
      );

      const out = {};
      results.forEach(([id, meta]) => {
        out[id] = meta;
      });
      return out;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshMsgMeta = useCallback(async () => {
    const ids = visibleProjects.map((p) => p._id);
    if (!ids.length) {
      setMsgMeta({});
      return;
    }
    const incoming = await hydrateMetaBatch(ids);
    setMsgMeta((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const inc = incoming[id] || {};
        const cur = prev[id] || {
          unread: 0,
          lastText: "",
          lastSender: "",
          lastAt: null,
        };
        const safeLastAt = inc.lastAt ? inc.lastAt : cur.lastAt || null;
        next[id] = {
          unread: typeof inc.unread === "number" ? inc.unread : cur.unread,
          lastText: inc.lastText != null ? inc.lastText : cur.lastText,
          lastSender: inc.lastSender != null ? inc.lastSender : cur.lastSender,
          lastAt: safeLastAt,
        };
      });
      return next;
    });
  }, [visibleProjects, hydrateMetaBatch]);

  useEffect(() => {
    refreshMsgMeta();
  }, [refreshMsgMeta, JSON.stringify(visibleProjects)]); // eslint-disable-line

  // periodic / focus refresh
  useEffect(() => {
    const onFocus = () => {
      refreshMsgMeta();
    };
    window.addEventListener("focus", onFocus);
    const t = setInterval(refreshMsgMeta, 20000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, [refreshMsgMeta]);

  // mark selected project read (do NOT touch lastAt)
  useEffect(() => {
    if (!(selectedProject && selectedProject._id)) return;
    const pid = selectedProject._id;
    const doMark = async () => {
      try {
        await api.post("/project-messages/" + pid + "/read", {}, authHeader);
      } catch {}
      setMsgMeta((prev) => {
        const cur = prev[pid] || {};
        const next = { ...prev };
        next[pid] = { ...cur, unread: 0 };
        return next;
      });
    };
    doMark();
  }, [selectedProject && selectedProject._id]); // eslint-disable-line

  // realtime: move project to top on new message
  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;

    const onIncoming = (msg) => {
      msg = msg || {};
      const pid = String(msg.project || msg.projectId || "");
      if (!pid) return;

      const pv = previewFromMessage(msg);
      setMsgMeta((prev) => {
        const cur = prev[pid] || {
          unread: 0,
          lastText: "",
          lastSender: "",
          lastAt: null,
        };
        const opened = selectedProject && String(selectedProject._id) === pid;
        const next = { ...prev };
        next[pid] = {
          unread: opened ? 0 : (cur.unread || 0) + 1,
          lastText: pv.lastText,
          lastSender: pv.lastSender,
          lastAt: msg.createdAt || new Date().toISOString(),
        };
        return next;
      });
      const senderId =
        typeof msg.sender === "object" ? msg.sender?._id : msg.sender;

      if (senderId !== authUser?._id && Notification.permission === "granted") {
        new Notification("New Project Message", {
          body:
            (msg.sender?.name || "Someone") +
            ": " +
            (msg.content || "📎 File received"),
        });
      }
    };

    socket.on?.("project:message", onIncoming);
    return () => {
      socket.off?.("project:message", onIncoming);
    };
  }, [selectedProject?._id, authUser?._id]); // eslint-disable-line

  // realtime: refresh projects list on membership changes
  useEffect(() => {
    const socket = getSocket?.();
    if (!socket || !refreshProjects) return;

    const onAdded = () => refreshProjects();
    const onRemoved = () => refreshProjects();

    socket.on?.("project:added", onAdded);
    socket.on?.("project:removed", onRemoved);
    return () => {
      socket.off?.("project:added", onAdded);
      socket.off?.("project:removed", onRemoved);
    };
  }, [refreshProjects]);

  /* ---------------- after assign ---------------- */
  const handleAfterAssign = async () => {
    if (refreshProjects) await refreshProjects();
    onAfterAssign();
    setTimeout(refreshMsgMeta, 0);
  };

  /* ---------------- back nav ---------------- */
  const navigate = useNavigate();
  const handleBackClick = () => {
    if (typeof onBack === "function") onBack();
    else navigate("/admin");
  };

  /* ---------------- Auto-select precedence ----------------
     1) URL/state (/?project=...) or location.state.openProjectName
     2) initialOpenProjectId / initialOpenProjectName (prop)
     3) lastProjectId (localStorage), desktop only
  ------------------------------------------------------------------*/
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!projects || projects.length === 0) return;
    if (selectedProject) return;

    const stateName =
      (location.state && location.state.openProjectName) || null;
    const queryName = searchParams.get("project");

    let found = null;

    // 1) URL/state
    const wantName = (stateName || queryName || "").trim().toLowerCase();
    if (wantName) {
      const exact = projects.find(
        (p) => getProjName(p).toLowerCase() === wantName,
      );
      found =
        exact ||
        projects.find((p) => getProjName(p).toLowerCase().includes(wantName));
    }

    // 2) Props
    if (!found && initialOpenProjectId) {
      found = projects.find(
        (p) => String(p._id) === String(initialOpenProjectId),
      );
    }
    if (!found && initialOpenProjectName) {
      const target = String(initialOpenProjectName).trim().toLowerCase();
      const exact = projects.find(
        (p) => getProjName(p).toLowerCase() === target,
      );
      found =
        exact ||
        projects.find((p) => getProjName(p).toLowerCase().includes(target));
    }

    // 3) lastProjectId (ONLY DESKTOP)
    if (!found && !isMobile) {
      const last = localStorage.getItem("lastProjectId");
      if (last) {
        found = projects.find((p) => String(p._id) === String(last));
      }
    }

    if (found) {
      setSelectedProject(found);
      try {
        localStorage.setItem("lastProjectId", String(found._id));
      } catch {}
      // normalize URL param so refresh keeps correct project
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set("project", getProjName(found));
          return next;
        },
        { replace: true },
      );
    } else {
      // clear stale lastProjectId if it doesn't exist anymore
      localStorage.removeItem("lastProjectId");
    }

    didAutoSelectRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projects,
    selectedProject,
    initialOpenProjectId,
    initialOpenProjectName,
    location.key,
    isMobile,
  ]);

  /* ---------------- ordering: newest first ---------------- */
  const orderedVisible = useMemo(() => {
    const withTimes = visibleProjects.map((p) => {
      const metaRow = msgMeta[p._id] || {};
      const at = metaRow.lastAt ? Date.parse(metaRow.lastAt) : 0;
      return { p, at: isNaN(at) ? 0 : at };
    });
    withTimes.sort((a, b) => {
      if (b.at !== a.at) return b.at - a.at; // newest first
      return getProjName(a.p).localeCompare(getProjName(b.p));
    });
    return withTimes.map((x) => x.p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjects, msgMeta]);

  // search AFTER ordering
  const filteredProjects = useMemo(() => {
    if (!debounced) return orderedVisible;
    return orderedVisible.filter((p) =>
      getProjName(p).toLowerCase().includes(debounced),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedVisible, debounced]);

  /* ---------------- render ---------------- */
  return (
    <div className={`ps-shell${isMobile ? " ps-shell--mobile" : ""}`}>
      {/* Sticky top: back + title + search */}
      <div className="ps-top">
        <div className="ps-backbar">
          <button
            type="button"
            onClick={handleBackClick}
            className="ps-back"
            title="Back to Dashboard"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </button>
        </div>

        <h4 className="ps-title">PROJECTS COMMUNICATION</h4>

        <div className="ps-search-wrap">
          <div className="ps-search">
            <svg viewBox="0 0 24 24" fill="none" className="ps-search-icon">
              <path
                d="M21 21l-4.2-4.2M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search projects…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchTerm("")}
                className="ps-search-clear"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="ps-list">
        {showSpinner ? (
          <p className="ps-msg">Loading...</p>
        ) : filteredProjects.length === 0 ? (
          <p className="ps-msg">No projects found.</p>
        ) : (
          filteredProjects.map((proj) => {
            const active = selectedProject && selectedProject._id === proj._id;
            const displayName = getProjName(proj);
            const counts = projectTaskCounts[proj._id] || {
              pending: 0,
              completed: 0,
            };
            const metaRow = msgMeta[proj._id] || {
              unread: 0,
              lastText: "",
              lastSender: "",
              lastAt: null,
            };

            // Prefer `members` (new route), fallback to `assignedUsers`
            const membersCount = Array.isArray(proj.members)
              ? proj.members.length
              : (proj.assignedUsers && proj.assignedUsers.length) || 0;

            const hasUnread = metaRow.unread > 0;

            return (
              <div
                key={proj._id}
                onClick={() => {
                  setSelectedProject(proj);
                  try {
                    localStorage.setItem("lastProjectId", String(proj._id));
                  } catch {}
                  // reflect selection in URL so next visit doesn't stick wrong one
                  setSearchParams(
                    (sp) => {
                      const next = new URLSearchParams(sp);
                      next.set("project", displayName);
                      return next;
                    },
                    { replace: true },
                  );
                  // instant local unread clear
                  setMsgMeta((prev) => ({
                    ...prev,
                    [proj._id]: { ...(prev[proj._id] || {}), unread: 0 },
                  }));
                }}
                className={`ps-card${active ? " ps-card--active" : ""}`}
              >
                {/* avatar */}
                <div className="ps-avatar">
                  {displayName.charAt(0).toUpperCase()}
                </div>

                {/* center */}
                <div className="ps-center">
                  <div className="ps-name-row">
                    <span className="ps-name" title={displayName}>
                      {displayName}
                    </span>
                    {hasUnread ? (
                      <span className="ps-badge">{metaRow.unread}</span>
                    ) : null}
                  </div>

                  <div
                    className={`ps-preview${
                      hasUnread ? " ps-preview--unread" : ""
                    }`}
                  >
                    {metaRow.lastSender
                      ? metaRow.lastSender + ": " + (metaRow.lastText || "")
                      : "No messages yet"}
                  </div>

                  <div className="ps-chips">
                    <span className="ps-chip">👥 {membersCount}</span>
                    <span className="ps-chip ps-chip--pending">
                      ⏳ {counts.pending} pending
                    </span>
                    <span className="ps-chip ps-chip--done">
                      ✓ {counts.completed} done
                    </span>
                  </div>

                  {canAssign ? (
                    <button
                      type="button"
                      className="ps-assign"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssignModalProject(proj);
                      }}
                    >
                      Assign User
                    </button>
                  ) : null}
                </div>

                {/* time */}
                <div
                  className={`ps-time${hasUnread ? " ps-time--unread" : ""}`}
                >
                  {metaRow.lastAt ? formatTime(metaRow.lastAt) : ""}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {assignModalProject ? (
        <AssignUserModal
          project={assignModalProject}
          onClose={() => {
            setAssignModalProject(null);
          }}
          onAssignSuccess={handleAfterAssign}
          users={users}
          drawingMaster={drawingMaster}
        />
      ) : null}
    </div>
  );
}